const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Obtener todos los usuarios
export async function getUsuarios(rol = '') {
  try {
    const params = new URLSearchParams();
    if (rol && String(rol).trim()) params.set('rol', String(rol).trim());
    const qs = params.toString();
    const res = await fetch(`${API_BASE}/sistema/usuarios/${qs ? `?${qs}` : ''}`, {
      headers: getAuthHeaders()
    });
    if (!res.ok) {
      console.error('Error fetching usuarios:', res.status);
      return [];
    }
    const data = await res.json();
    
    // Mapear datos del backend al formato del frontend
    return Array.isArray(data) ? data.map(u => ({
      id: u.id_usuario,
      nombre: u.nombre_completo,
      usuario: u.usuario,
      email: u.email,
      rol: u.rol_nombre,
      id_rol: u.id_rol,
      institucion: u.institucion_nombre,
      id_institucion: u.id_institucion,
      campus: u.campus_nombres,
      campus_asignados: u.campus_asignados || [],
      secciones_asignadas: u.secciones_asignadas || [],
      estado: u.activo ? 'ACTIVO' : 'INACTIVO',
      activo: u.activo,
      ultimo_acceso: u.ultimo_acceso ? new Date(u.ultimo_acceso).toLocaleDateString() : '—',
      fecha_creacion: u.fecha_creacion
    })) : [];
  } catch (error) {
    console.error('Error in getUsuarios:', error);
    return [];
  }
}

// Obtener usuarios con paginación para listados rápidos
export async function getUsuariosPaged({ page = 1, page_size = 10, search = '', rol = '' } = {}) {
  try {
    const params = new URLSearchParams();
    if (page) params.set('page', String(page));
    if (page_size) params.set('page_size', String(page_size));
    if (search && search.trim()) params.set('search', search.trim());
    if (rol && rol.trim()) params.set('rol', rol.trim());

    const res = await fetch(`${API_BASE}/sistema/usuarios/listado/?${params.toString()}`, {
      headers: getAuthHeaders()
    });

    if (!res.ok) {
      console.error('Error fetching usuarios paged:', res.status);
      return { count: 0, page, page_size, total_pages: 1, results: [] };
    }

    const data = await res.json();
    const results = Array.isArray(data.results) ? data.results.map(u => ({
      id: u.id_usuario,
      nombre: u.nombre_completo,
      usuario: u.usuario,
      email: u.email,
      rol: u.rol_nombre,
      id_rol: u.id_rol,
      institucion: u.institucion_nombre,
      id_institucion: u.id_institucion,
      campus: u.campus_nombres,
      estado: u.activo ? 'ACTIVO' : 'INACTIVO',
      activo: u.activo,
      ultimo_acceso: u.ultimo_acceso ? new Date(u.ultimo_acceso).toLocaleDateString() : '—',
      fecha_creacion: u.fecha_creacion,
    })) : [];

    return {
      count: data.count || results.length,
      page: data.page || page,
      page_size: data.page_size || page_size,
      total_pages: data.total_pages || Math.max(1, Math.ceil((data.count || results.length) / page_size)),
      results,
    };
  } catch (error) {
    console.error('Error in getUsuariosPaged:', error);
    return { count: 0, page, page_size, total_pages: 1, results: [] };
  }
}

// Obtener un usuario por ID
export async function getUsuario(id) {
  try {
    const res = await fetch(`${API_BASE}/sistema/usuarios/${id}/`, {
      headers: getAuthHeaders()
    });
    if (!res.ok) {
      throw new Error(`Error fetching usuario: ${res.status}`);
    }
    const data = await res.json();
    
    // Mapear datos del backend al formato del frontend
    return {
      id: data.id_usuario,
      id_usuario: data.id_usuario,
      nombre: data.nombre_completo?.split(' ')[0] || data.nombre_completo,
      apellidos: data.nombre_completo?.split(' ').slice(1).join(' ') || '',
      nombre_completo: data.nombre_completo,
      usuario: data.usuario,
      email: data.email,
      email_institucional: data.email,
      rol: data.rol_nombre,
      id_rol: data.id_rol,
      institucion: data.institucion_nombre,
      institucion_nombre: data.institucion_nombre,
      id_institucion: data.id_institucion,
      campus: data.campus_nombres,
      campus_asignados: data.campus_asignados || [],
      secciones_asignadas: data.secciones_asignadas || [],
      estado: data.activo ? 'ACTIVO' : 'INACTIVO',
      activo: data.activo,
      ultimo_acceso: data.ultimo_acceso,
      fecha_creacion: data.fecha_creacion,
      permisos: data.permisos || {}
    };
  } catch (error) {
    console.error('Error in getUsuario:', error);
    throw error;
  }
}

// Crear un nuevo usuario
export async function createUsuario(userData) {
  try {
    const payload = {
      nombre_completo: userData.nombre_completo,
      usuario: userData.usuario,
      email: userData.email,
      id_rol: userData.id_rol,
      id_institucion: userData.id_institucion,
      activo: userData.activo !== undefined ? userData.activo : true,
      campus_ids: userData.campus_ids || [],
      seccion_ids: userData.seccion_ids || []
    };

    // Solo incluir contraseña si se proporciona
    if (userData.password) {
      payload.password = userData.password;
    }

    const res = await fetch(`${API_BASE}/sistema/usuarios/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      let message = 'Error al crear usuario';
      try {
        const errorData = await res.json();
        message = errorData.detail || JSON.stringify(errorData) || message;
      } catch (e) {
        const text = await res.text().catch(() => '');
        if (text) message = text;
      }
      throw new Error(message);
    }

    return await res.json();
  } catch (error) {
    console.error('Error in createUsuario:', error);
    throw error;
  }
}

// Actualizar un usuario existente
export async function updateUsuario(id, userData) {
  try {
    const payload = {
      nombre_completo: userData.nombre_completo,
      usuario: userData.usuario,
      email: userData.email,
      id_rol: userData.id_rol,
      id_institucion: userData.id_institucion,
      activo: userData.activo !== undefined ? userData.activo : true,
      campus_ids: userData.campus_ids || [],
      seccion_ids: userData.seccion_ids || []
    };

    // Solo incluir contraseña si se proporciona
    if (userData.password) {
      payload.password = userData.password;
    }

    const res = await fetch(`${API_BASE}/sistema/usuarios/${id}/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      let message = 'Error al actualizar usuario';
      try {
        const errorData = await res.json();
        message = errorData.detail || JSON.stringify(errorData) || message;
      } catch (e) {
        const text = await res.text().catch(() => '');
        if (text) message = text;
      }
      throw new Error(message);
    }

    return await res.json();
  } catch (error) {
    console.error('Error in updateUsuario:', error);
    throw error;
  }
}

// Eliminar un usuario
export async function deleteUsuario(id) {
  try {
    const res = await fetch(`${API_BASE}/sistema/usuarios/${id}/`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Error al eliminar usuario');
    }

    return true;
  } catch (error) {
    console.error('Error in deleteUsuario:', error);
    throw error;
  }
}

// ============================================================================
// GESTIÓN DE MÚLTIPLES ROLES POR USUARIO
// ============================================================================

/**
 * Obtener todos los roles activos de un usuario.
 */
export async function getRolesUsuario(idUsuario) {
  try {
    const res = await fetch(`${API_BASE}/sistema/usuarios-roles/por-usuario/${idUsuario}/`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error('Error al obtener roles del usuario');
    return res.json();
  } catch (error) {
    console.error('Error in getRolesUsuario:', error);
    return [];
  }
}

/**
 * Asignar un nuevo rol a un usuario.
 */
export async function asignarRolUsuario(idUsuario, idRol, esPrincipal = false, observaciones = '') {
  const res = await fetch(`${API_BASE}/sistema/usuarios-roles/asignar/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({
      id_usuario: idUsuario,
      id_rol: idRol,
      es_rol_principal: esPrincipal,
      observaciones,
    }),
  });
    if (!res.ok) {
      const text = await res.text();
      let msg = 'Error al asignar rol';
      try {
        const err = JSON.parse(text);
        msg = err.error || err.detail || JSON.stringify(err);
      } catch {
        msg = text.slice(0, 300);
      }
      console.error('[asignarRolUsuario] Respuesta del servidor:', text.slice(0, 500));
      throw new Error(msg);
    }
  return res.json();
}

/**
 * Revocar un rol de un usuario.
 */
export async function revocarRolUsuario(idUsuarioRol) {
  const res = await fetch(`${API_BASE}/sistema/usuarios-roles/${idUsuarioRol}/revocar/`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Error al revocar rol');
  return res.json();
}
